import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OptionProp } from '../../shared/normalize-options';

type MoveDirection = 1 | -1 | 'home' | 'end';

type UseTypeaheadNavigationParams = {
    options: OptionProp[];
};

type UseTypeaheadNavigationResult = {
    activeIndex: number;
    firstSelectableIndex: number;
    lastSelectableIndex: number;
    moveActive: (direction: MoveDirection) => number;
    setActiveIndex: (index: number) => void;
    getNextSelectableIndex: (currentIndex: number) => number;
    getPreviousSelectableIndex: (currentIndex: number) => number;
    requestOptionFocus: (index: number) => void;
    consumeFocusRequest: () => number | undefined;
};

const buildSelectableIndexes = (options: OptionProp[]) => {
    return options.reduce<number[]>((acc, option, index) => {
        if (!option.disabled) {
            acc.push(index);
        }
        return acc;
    }, []);
};

const useTypeaheadNavigation = ({ options }: UseTypeaheadNavigationParams): UseTypeaheadNavigationResult => {
    const selectableIndexes = useMemo(() => buildSelectableIndexes(options), [options]);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const focusRequestRef = useRef<number | undefined>();

    useEffect(() => {
        if (activeIndex === -1) return;
        if (options[activeIndex] && !options[activeIndex].disabled) return;
        const fallback = selectableIndexes[0] ?? -1;
        setActiveIndex(fallback);
    }, [activeIndex, options, selectableIndexes]);

    const firstSelectableIndex = selectableIndexes[0] ?? -1;
    const lastSelectableIndex = selectableIndexes[selectableIndexes.length - 1] ?? -1;

    const moveActive = useCallback(
        (direction: MoveDirection) => {
            if (!selectableIndexes.length) {
                setActiveIndex(-1);
                return -1;
            }

            if (direction === 'home') {
                const next = firstSelectableIndex;
                setActiveIndex(next);
                return next;
            }

            if (direction === 'end') {
                const next = lastSelectableIndex;
                setActiveIndex(next);
                return next;
            }

            if (activeIndex === -1) {
                const next = direction === 1 ? firstSelectableIndex : lastSelectableIndex;
                setActiveIndex(next);
                return next;
            }

            const currentPosition = selectableIndexes.indexOf(activeIndex);
            const wrappedPosition = (currentPosition + direction + selectableIndexes.length) % selectableIndexes.length;
            const next = selectableIndexes[wrappedPosition];
            setActiveIndex(next);
            return next;
        },
        [activeIndex, firstSelectableIndex, lastSelectableIndex, selectableIndexes]
    );

    const getNextSelectableIndex = useCallback(
        (currentIndex: number) => {
            if (!selectableIndexes.length) return -1;
            const position = selectableIndexes.indexOf(currentIndex);
            if (position === -1) return selectableIndexes[0];
            return selectableIndexes[(position + 1) % selectableIndexes.length];
        },
        [selectableIndexes]
    );

    const getPreviousSelectableIndex = useCallback(
        (currentIndex: number) => {
            if (!selectableIndexes.length) return -1;
            const position = selectableIndexes.indexOf(currentIndex);
            if (position === -1) return selectableIndexes[selectableIndexes.length - 1];
            return selectableIndexes[(position - 1 + selectableIndexes.length) % selectableIndexes.length];
        },
        [selectableIndexes]
    );

    const requestOptionFocus = useCallback((index: number) => {
        focusRequestRef.current = index;
    }, []);

    const consumeFocusRequest = useCallback(() => {
        const next = focusRequestRef.current;
        focusRequestRef.current = undefined;
        return next;
    }, []);

    return {
        activeIndex,
        firstSelectableIndex,
        lastSelectableIndex,
        moveActive,
        setActiveIndex,
        getNextSelectableIndex,
        getPreviousSelectableIndex,
        requestOptionFocus,
        consumeFocusRequest,
    };
};

export type { MoveDirection, UseTypeaheadNavigationParams, UseTypeaheadNavigationResult };
export { useTypeaheadNavigation };
